import React, { Suspense } from 'react';
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from 'react-router';
import {
  Box,
  Code,
  ColorSchemeScript,
  Container,
  mantineHtmlProps,
  Text,
  Title,
} from '@mantine/core';

// Import styles of packages that you've installed.
// All packages except `@mantine/hooks` require styles imports
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dropzone/styles.css';
import '@mantine/carousel/styles.css';
import '@mantine/code-highlight/styles.css';
import '@mantine/tiptap/styles.css';
import '@mantine/spotlight/styles.css';
import '@mantine/nprogress/styles.css';
import { Notifications } from '@mantine/notifications';
import { ModalsProvider } from '@mantine/modals';

import { MantineProvider } from '@app/providers';
import '@app/fonts/index.scss';
import 'mantine-datatable/styles.layer.css';
import '@mantine/core/styles.layer.css';

import type { Route } from './+types/root';
import './app.css';
import { BreadcrumbsProvider } from '@shared/lib';
import { QueryProvider } from '@app/providers/react-query';
import { SessionProvider } from '@app/session';

import './i18n';
import { useMounted } from '@mantine/hooks';
import { FeatureFlagsProvider } from '@packages/feature-flags/react';
import { FeatureFlagsDevtools, featureFlags } from '@app/feature-flags';

export function Layout({ children }: { children: React.ReactNode }) {
  const isMounted = useMounted();

  return (
    <html lang='fr' {...mantineHtmlProps}>
      <head>
        <meta charSet='utf-8' />
        <meta name='viewport' content='width=device-width, initial-scale=1' />
        <link rel='icon' href='/favicon.svg' type='image/svg' />
        {/* Settings of the stand, before the app starts: @packages/runtime-config. */}
        <script src='/config.js' />
        <ColorSchemeScript />
        <Meta />
        <Links />
      </head>
      <body>
        <QueryProvider>
          <SessionProvider>
            <MantineProvider>
              <ModalsProvider>
                <FeatureFlagsProvider flags={featureFlags}>
                  <Notifications />
                  <BreadcrumbsProvider>{children}</BreadcrumbsProvider>
                  {/* The panel portals into document.body: not in the prerendered shell. */}
                  {isMounted && FeatureFlagsDevtools && (
                    <Suspense fallback={null}>
                      <FeatureFlagsDevtools />
                    </Suspense>
                  )}
                </FeatureFlagsProvider>
              </ModalsProvider>
            </MantineProvider>
          </SessionProvider>
          <ScrollRestoration />
          <Scripts />
        </QueryProvider>
      </body>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = 'Oops!';
  let details = 'An unexpected error occurred.';
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? '404' : 'Error';
    details =
      error.status === 404
        ? 'The requested page could not be found.'
        : error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <Container component='main' pt='xl' p='md' mx='auto'>
      <Title>{message}</Title>
      <Text>{details}</Text>
      {stack && (
        <Box component='pre' w='100%' style={{ overflowX: 'auto' }} p='md'>
          <Code>{stack}</Code>
        </Box>
      )}
    </Container>
  );
}
